<template>
  <div class="modal fade" id="addIntegrationModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="mdi mdi-plus-circle-outline me-2"></i>
            Add New System Integration
          </h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <!-- Integration Type Selection -->
          <div v-if="currentStep === 'type'" class="step-content">
            <div class="text-center mb-4">
              <h5>Select Integration Type</h5>
              <p class="text-muted">Choose the type of system you want to integrate with</p>
            </div>

            <div class="row">
              <!-- County Financial Systems -->
              <div class="col-md-6 mb-4">
                <div class="integration-type-card">
                  <div class="card h-100 border-2" :class="{ 'border-primary': selectedCategory === 'COUNTY_FINANCIAL' }"
                       @click="selectCategory('COUNTY_FINANCIAL')">
                    <div class="card-header bg-success text-white text-center">
                      <i class="mdi mdi-bank fs-1"></i>
                      <h5 class="mb-0 text-white mt-2">County Financial Systems</h5>
                    </div>
                    <div class="card-body">
                      <div class="system-options">
                        <div v-for="system in countyFinancialSystems" :key="system.id"
                             class="system-option mb-3" @click.stop="selectSystem(system)">
                          <div class="d-flex align-items-center">
                            <div class="form-check me-3">
                              <input :checked="selectedSystem?.id === system.id" class="form-check-input"
                                     type="radio" :id="'county-' + system.id">
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-1">{{ system.name }}</h6>
                              <small class="text-muted">{{ system.description }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Government Systems -->
              <div class="col-md-6 mb-4">
                <div class="integration-type-card">
                  <div class="card h-100 border-2" :class="{ 'border-primary': selectedCategory === 'GOVERNMENT' }"
                       @click="selectCategory('GOVERNMENT')">
                    <div class="card-header bg-info text-white text-center">
                      <i class="mdi mdi-shield-account fs-1"></i>
                      <h5 class="mb-0 text-white mt-2">Government Systems</h5>
                    </div>
                    <div class="card-body">
                      <div class="system-options">
                        <div v-for="system in governmentSystems" :key="system.id"
                             class="system-option mb-3" @click.stop="selectSystem(system)">
                          <div class="d-flex align-items-center">
                            <div class="form-check me-3">
                              <input :checked="selectedSystem?.id === system.id" class="form-check-input"
                                     type="radio" :id="'gov-' + system.id">
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-1">{{ system.name }}</h6>
                              <small class="text-muted">{{ system.description }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Payment Systems -->
              <div class="col-md-6 mb-4">
                <div class="integration-type-card">
                  <div class="card h-100 border-2" :class="{ 'border-primary': selectedCategory === 'PAYMENT' }"
                       @click="selectCategory('PAYMENT')">
                    <div class="card-header bg-warning text-dark text-center">
                      <i class="mdi mdi-credit-card fs-1"></i>
                      <h5 class="mb-0 mt-2">Payment Systems</h5>
                    </div>
                    <div class="card-body">
                      <div class="system-options">
                        <div v-for="system in paymentSystems" :key="system.id"
                             class="system-option mb-3" @click.stop="selectSystem(system)">
                          <div class="d-flex align-items-center">
                            <div class="form-check me-3">
                              <input :checked="selectedSystem?.id === system.id" class="form-check-input"
                                     type="radio" :id="'payment-' + system.id">
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-1">{{ system.name }}</h6>
                              <small class="text-muted">{{ system.description }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Third Party Systems -->
              <div class="col-md-6 mb-4">
                <div class="integration-type-card">
                  <div class="card h-100 border-2" :class="{ 'border-primary': selectedCategory === 'THIRD_PARTY' }"
                       @click="selectCategory('THIRD_PARTY')">
                    <div class="card-header bg-secondary text-white text-center">
                      <i class="mdi mdi-api fs-1"></i>
                      <h5 class="mb-0 text-white mt-2">Third Party / Custom</h5>
                    </div>
                    <div class="card-body">
                      <div class="system-options">
                        <div v-for="system in thirdPartySystems" :key="system.id"
                             class="system-option mb-3" @click.stop="selectSystem(system)">
                          <div class="d-flex align-items-center">
                            <div class="form-check me-3">
                              <input :checked="selectedSystem?.id === system.id" class="form-check-input"
                                     type="radio" :id="'third-' + system.id">
                            </div>
                            <div class="flex-grow-1">
                              <h6 class="mb-1">{{ system.name }}</h6>
                              <small class="text-muted">{{ system.description }}</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Basic Configuration -->
          <div v-if="currentStep === 'config'" class="step-content">
            <div class="text-center mb-4">
              <h5>Basic Configuration</h5>
              <p class="text-muted">Configure the basic settings for {{ selectedSystem?.name }}</p>
            </div>

            <form @submit.prevent="validateConfiguration">
              <div class="row">
                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">System Information</h6>
                    </div>
                    <div class="card-body">
                      <div class="mb-3">
                        <label class="form-label">System Name <span class="text-danger">*</span></label>
                        <input v-model="integrationForm.systemName" type="text" class="form-control"
                               :placeholder="selectedSystem?.name" required>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Description</label>
                        <textarea v-model="integrationForm.description" class="form-control" rows="3"
                                  :placeholder="selectedSystem?.description"></textarea>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">API Endpoint <span class="text-danger">*</span></label>
                        <input v-model="integrationForm.apiEndpoint" type="url" class="form-control"
                               :placeholder="selectedSystem?.defaultEndpoint" required>
                        <small class="text-muted">The base URL for the API endpoint</small>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">API Version</label>
                        <select v-model="integrationForm.apiVersion" class="form-select">
                          <option value="v1">Version 1.0</option>
                          <option value="v2">Version 2.0</option>
                          <option value="v3">Version 3.0</option>
                          <option value="latest">Latest</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Integration Settings</h6>
                    </div>
                    <div class="card-body">
                      <div class="mb-3">
                        <label class="form-label">Sync Frequency</label>
                        <select v-model="integrationForm.syncFrequency" class="form-select">
                          <option value="REAL_TIME">Real-time</option>
                          <option value="HOURLY">Every Hour</option>
                          <option value="DAILY">Daily</option>
                          <option value="WEEKLY">Weekly</option>
                          <option value="MANUAL">Manual Only</option>
                        </select>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Data Flow Direction</label>
                        <div class="form-check-group">
                          <div class="form-check">
                            <input v-model="integrationForm.dataFlow" class="form-check-input"
                                   type="checkbox" value="IN" id="dataFlowIn">
                            <label class="form-check-label" for="dataFlowIn">
                              <i class="mdi mdi-arrow-down text-success me-1"></i>
                              Inbound (Receive Data)
                            </label>
                          </div>
                          <div class="form-check">
                            <input v-model="integrationForm.dataFlow" class="form-check-input"
                                   type="checkbox" value="OUT" id="dataFlowOut">
                            <label class="form-check-label" for="dataFlowOut">
                              <i class="mdi mdi-arrow-up text-warning me-1"></i>
                              Outbound (Send Data)
                            </label>
                          </div>
                        </div>
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Connection Timeout (seconds)</label>
                        <input v-model="integrationForm.timeout" type="number" class="form-control"
                               min="5" max="300" value="30">
                      </div>

                      <div class="mb-3">
                        <label class="form-label">Retry Attempts</label>
                        <input v-model="integrationForm.retryAttempts" type="number" class="form-control"
                               min="0" max="10" value="3">
                      </div>

                      <div class="mb-3">
                        <div class="form-check form-switch">
                          <input v-model="integrationForm.enableLogging" class="form-check-input"
                                 type="checkbox" id="enableConfigLogging">
                          <label class="form-check-label" for="enableConfigLogging">
                            Enable detailed logging
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <!-- Authentication Setup -->
          <div v-if="currentStep === 'auth'" class="step-content">
            <div class="text-center mb-4">
              <h5>Authentication Setup</h5>
              <p class="text-muted">Configure authentication for {{ selectedSystem?.name }}</p>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Authentication Method</h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <label class="form-label">Authentication Type <span class="text-danger">*</span></label>
                      <select v-model="authForm.authType" @change="handleAuthTypeChange" class="form-select" required>
                        <option value="">Select authentication method</option>
                        <option value="API_KEY">API Key</option>
                        <option value="OAUTH2">OAuth 2.0</option>
                        <option value="BASIC_AUTH">Basic Authentication</option>
                        <option value="BEARER_TOKEN">Bearer Token</option>
                        <option value="CUSTOM">Custom Headers</option>
                        <option value="NONE">No Authentication</option>
                      </select>
                    </div>

                    <!-- API Key Authentication -->
                    <div v-if="authForm.authType === 'API_KEY'">
                      <div class="mb-3">
                        <label class="form-label">API Key <span class="text-danger">*</span></label>
                        <div class="input-group">
                          <input v-model="authForm.apiKey" :type="showCredentials.apiKey ? 'text' : 'password'"
                                 class="form-control" placeholder="Enter your API key" required>
                          <button @click="showCredentials.apiKey = !showCredentials.apiKey" type="button"
                                  class="btn btn-outline-secondary">
                            <i :class="showCredentials.apiKey ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                          </button>
                        </div>
                      </div>
                      <div class="mb-3">
                        <label class="form-label">Header Name</label>
                        <input v-model="authForm.apiKeyHeader" type="text" class="form-control"
                               placeholder="X-API-Key" value="X-API-Key">
                      </div>
                    </div>

                    <!-- OAuth 2.0 Authentication -->
                    <div v-if="authForm.authType === 'OAUTH2'">
                      <div class="mb-3">
                        <label class="form-label">Client ID <span class="text-danger">*</span></label>
                        <input v-model="authForm.clientId" type="text" class="form-control" required>
                      </div>
                      <div class="mb-3">
                        <label class="form-label">Client Secret <span class="text-danger">*</span></label>
                        <div class="input-group">
                          <input v-model="authForm.clientSecret" :type="showCredentials.clientSecret ? 'text' : 'password'"
                                 class="form-control" required>
                          <button @click="showCredentials.clientSecret = !showCredentials.clientSecret" type="button"
                                  class="btn btn-outline-secondary">
                            <i :class="showCredentials.clientSecret ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                          </button>
                        </div>
                      </div>
                      <div class="mb-3">
                        <label class="form-label">Authorization URL</label>
                        <input v-model="authForm.authUrl" type="url" class="form-control"
                               :placeholder="selectedSystem?.defaultAuthUrl">
                      </div>
                      <div class="mb-3">
                        <label class="form-label">Token URL <span class="text-danger">*</span></label>
                        <input v-model="authForm.tokenUrl" type="url" class="form-control"
                               :placeholder="selectedSystem?.defaultTokenUrl" required>
                      </div>
                      <div class="mb-3">
                        <label class="form-label">Scope</label>
                        <input v-model="authForm.scope" type="text" class="form-control"
                               placeholder="read write">
                      </div>
                    </div>

                    <!-- Basic Authentication -->
                    <div v-if="authForm.authType === 'BASIC_AUTH'">
                      <div class="mb-3">
                        <label class="form-label">Username <span class="text-danger">*</span></label>
                        <input v-model="authForm.username" type="text" class="form-control" required>
                      </div>
                      <div class="mb-3">
                        <label class="form-label">Password <span class="text-danger">*</span></label>
                        <div class="input-group">
                          <input v-model="authForm.password" :type="showCredentials.password ? 'text' : 'password'"
                                 class="form-control" required>
                          <button @click="showCredentials.password = !showCredentials.password" type="button"
                                  class="btn btn-outline-secondary">
                            <i :class="showCredentials.password ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Bearer Token -->
                    <div v-if="authForm.authType === 'BEARER_TOKEN'">
                      <div class="mb-3">
                        <label class="form-label">Bearer Token <span class="text-danger">*</span></label>
                        <div class="input-group">
                          <input v-model="authForm.bearerToken" :type="showCredentials.bearerToken ? 'text' : 'password'"
                                 class="form-control" required>
                          <button @click="showCredentials.bearerToken = !showCredentials.bearerToken" type="button"
                                  class="btn btn-outline-secondary">
                            <i :class="showCredentials.bearerToken ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Custom Headers -->
                    <div v-if="authForm.authType === 'CUSTOM'">
                      <div class="mb-3">
                        <label class="form-label">Custom Headers</label>
                        <div v-for="(header, index) in authForm.customHeaders" :key="index" class="row mb-2">
                          <div class="col-5">
                            <input v-model="header.name" type="text" class="form-control" placeholder="Header Name">
                          </div>
                          <div class="col-5">
                            <input v-model="header.value" type="text" class="form-control" placeholder="Header Value">
                          </div>
                          <div class="col-2">
                            <button @click="removeCustomHeader(index)" type="button" class="btn btn-outline-danger">
                              <i class="mdi mdi-close"></i>
                            </button>
                          </div>
                        </div>
                        <button @click="addCustomHeader" type="button" class="btn btn-outline-primary btn-sm">
                          <i class="mdi mdi-plus me-1"></i>Add Header
                        </button>
                      </div>
                    </div>

                    <!-- No Authentication -->
                    <div v-if="authForm.authType === 'NONE'" class="alert alert-warning">
                      <i class="mdi mdi-alert-triangle me-2"></i>
                      <strong>Warning:</strong> This integration will not use any authentication.
                      Ensure the API endpoint is publicly accessible or uses other security measures.
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Connection Test</h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <button @click="testConnection" :disabled="testing || !canTestConnection"
                              class="btn btn-info w-100">
                        <span v-if="!testing">
                          <i class="mdi mdi-connection me-1"></i>
                          Test Connection
                        </span>
                        <span v-else>
                          <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                          Testing Connection...
                        </span>
                      </button>
                      <small class="text-muted d-block mt-2">
                        Test the connection with the provided configuration
                      </small>
                    </div>

                    <!-- Test Results -->
                    <div v-if="connectionTestResult" class="alert"
                         :class="connectionTestResult.success ? 'alert-success' : 'alert-danger'">
                      <h6 class="alert-heading">
                        <i :class="connectionTestResult.success ? 'mdi mdi-check-circle' : 'mdi mdi-alert-circle'" class="me-2"></i>
                        {{ connectionTestResult.success ? 'Connection Successful' : 'Connection Failed' }}
                      </h6>
                      <p class="mb-0">{{ connectionTestResult.message }}</p>
                      <div v-if="connectionTestResult.details" class="mt-2">
                        <small>
                          <strong>Response Time:</strong> {{ connectionTestResult.details.responseTime }}ms<br>
                          <strong>Status Code:</strong> {{ connectionTestResult.details.statusCode }}<br>
                          <strong>Server:</strong> {{ connectionTestResult.details.server || 'Unknown' }}
                        </small>
                      </div>
                    </div>

                    <!-- Authentication Tips -->
                    <div class="mt-4">
                      <h6>Authentication Tips</h6>
                      <div class="tips-content">
                        <div v-if="authForm.authType === 'API_KEY'" class="alert alert-info alert-sm">
                          <small>
                            <i class="mdi mdi-lightbulb me-1"></i>
                            API keys are usually found in your account settings or developer console of the external system.
                          </small>
                        </div>
                        <div v-if="authForm.authType === 'OAUTH2'" class="alert alert-info alert-sm">
                          <small>
                            <i class="mdi mdi-lightbulb me-1"></i>
                            OAuth 2.0 requires registering your application with the external system to get client credentials.
                          </small>
                        </div>
                        <div v-if="authForm.authType === 'BASIC_AUTH'" class="alert alert-info alert-sm">
                          <small>
                            <i class="mdi mdi-lightbulb me-1"></i>
                            Use your regular username and password for the external system.
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Review & Confirm -->
          <div v-if="currentStep === 'review'" class="step-content">
            <div class="text-center mb-4">
              <h5>Review Integration Configuration</h5>
              <p class="text-muted">Please review your configuration before creating the integration</p>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">System Information</h6>
                  </div>
                  <div class="card-body">
                    <table class="table table-sm table-borderless">
                      <tr>
                        <td><strong>System Name:</strong></td>
                        <td>{{ integrationForm.systemName }}</td>
                      </tr>
                      <tr>
                        <td><strong>Category:</strong></td>
                        <td>{{ formatCategory(selectedCategory) }}</td>
                      </tr>
                      <tr>
                        <td><strong>API Endpoint:</strong></td>
                        <td class="text-truncate">{{ integrationForm.apiEndpoint }}</td>
                      </tr>
                      <tr>
                        <td><strong>API Version:</strong></td>
                        <td>{{ integrationForm.apiVersion }}</td>
                      </tr>
                      <tr>
                        <td><strong>Sync Frequency:</strong></td>
                        <td>{{ integrationForm.syncFrequency }}</td>
                      </tr>
                      <tr>
                        <td><strong>Data Flow:</strong></td>
                        <td>{{ formatDataFlow(integrationForm.dataFlow) }}</td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Authentication</h6>
                  </div>
                  <div class="card-body">
                    <table class="table table-sm table-borderless">
                      <tr>
                        <td><strong>Auth Type:</strong></td>
                        <td>{{ formatAuthType(authForm.authType) }}</td>
                      </tr>
                      <tr v-if="authForm.authType === 'API_KEY'">
                        <td><strong>Header Name:</strong></td>
                        <td>{{ authForm.apiKeyHeader }}</td>
                      </tr>
                      <tr v-if="authForm.authType === 'OAUTH2'">
                        <td><strong>Client ID:</strong></td>
                        <td class="text-truncate">{{ authForm.clientId }}</td>
                      </tr>
                      <tr v-if="authForm.authType === 'BASIC_AUTH'">
                        <td><strong>Username:</strong></td>
                        <td>{{ authForm.username }}</td>
                      </tr>
                      <tr>
                        <td><strong>Connection Status:</strong></td>
                        <td>
                          <span v-if="connectionTestResult?.success" class="badge bg-success">Tested Successfully</span>
                          <span v-else-if="connectionTestResult && !connectionTestResult.success" class="badge bg-danger">Test Failed</span>
                          <span v-else class="badge bg-warning">Not Tested</span>
                        </td>
                      </tr>
                    </table>

                    <div class="alert alert-warning mt-3">
                      <small>
                        <i class="mdi mdi-shield-check me-1"></i>
                        All authentication credentials will be encrypted and securely stored.
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row mt-3">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h6 class="mb-0">Next Steps</h6>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-4">
                        <div class="d-flex align-items-center mb-2">
                          <div class="step-number bg-primary text-white rounded-circle me-3">1</div>
                          <div>
                            <strong>Integration Created</strong>
                            <small class="d-block text-muted">System will be added to your integrations list</small>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="d-flex align-items-center mb-2">
                          <div class="step-number bg-info text-white rounded-circle me-3">2</div>
                          <div>
                            <strong>Initial Sync</strong>
                            <small class="d-block text-muted">First data synchronization will be performed</small>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="d-flex align-items-center mb-2">
                          <div class="step-number bg-success text-white rounded-circle me-3">3</div>
                          <div>
                            <strong>Configuration</strong>
                            <small class="d-block text-muted">Fine-tune settings and data mapping</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step Navigation -->
          <div class="step-navigation mt-4">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <!-- Step Indicators -->
                <div class="step-indicators d-flex align-items-center">
                  <div class="step-indicator" :class="{ active: currentStep === 'type', completed: stepCompleted.type }">
                    <div class="step-circle">
                      <i v-if="stepCompleted.type" class="mdi mdi-check"></i>
                      <span v-else>1</span>
                    </div>
                    <span class="step-label">Select Type</span>
                  </div>
                  <div class="step-connector"></div>
                  <div class="step-indicator" :class="{ active: currentStep === 'config', completed: stepCompleted.config }">
                    <div class="step-circle">
                      <i v-if="stepCompleted.config" class="mdi mdi-check"></i>
                      <span v-else>2</span>
                    </div>
                    <span class="step-label">Configuration</span>
                  </div>
                  <div class="step-connector"></div>
                  <div class="step-indicator" :class="{ active: currentStep === 'auth', completed: stepCompleted.auth }">
                    <div class="step-circle">
                      <i v-if="stepCompleted.auth" class="mdi mdi-check"></i>
                      <span v-else>3</span>
                    </div>
                    <span class="step-label">Authentication</span>
                  </div>
                  <div class="step-connector"></div>
                  <div class="step-indicator" :class="{ active: currentStep === 'review', completed: stepCompleted.review }">
                    <div class="step-circle">
                      <i v-if="stepCompleted.review" class="mdi mdi-check"></i>
                      <span v-else>4</span>
                    </div>
                    <span class="step-label">Review</span>
                  </div>
                </div>
              </div>

              <div class="step-buttons">
                <button v-if="currentStep !== 'type'" @click="previousStep" type="button" class="btn btn-outline-secondary me-2">
                  <i class="mdi mdi-arrow-left me-1"></i>
                  Previous
                </button>
                <button v-if="currentStep !== 'review'" @click="nextStep" :disabled="!canProceed"
                        type="button" class="btn btn-primary">
                  Next
                  <i class="mdi mdi-arrow-right ms-1"></i>
                </button>
                <button v-if="currentStep === 'review'" @click="createIntegration" :disabled="creating"
                        type="button" class="btn btn-success">
                  <span v-if="!creating">
                    <i class="mdi mdi-check me-1"></i>
                    Create Integration
                  </span>
                  <span v-else>
                    <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                    Creating...
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: "AddIntegrationModal",
  data() {
    return {
      currentStep: 'type',
      testing: false,
      creating: false,
      selectedCategory: '',
      selectedSystem: null,
      connectionTestResult: null,

      // Step completion tracking
      stepCompleted: {
        type: false,
        config: false,
        auth: false,
        review: false
      },

      // Credential visibility
      showCredentials: {
        apiKey: false,
        clientSecret: false,
        password: false,
        bearerToken: false
      },

      // Integration form
      integrationForm: {
        systemName: '',
        description: '',
        apiEndpoint: '',
        apiVersion: 'v1',
        syncFrequency: 'DAILY',
        dataFlow: [],
        timeout: 30,
        retryAttempts: 3,
        enableLogging: true
      },

      // Authentication form
      authForm: {
        authType: '',
        apiKey: '',
        apiKeyHeader: 'X-API-Key',
        clientId: '',
        clientSecret: '',
        authUrl: '',
        tokenUrl: '',
        scope: '',
        username: '',
        password: '',
        bearerToken: '',
        customHeaders: []
      },

      // Available systems
      countyFinancialSystems: [
        {
          id: 'ifmis',
          name: 'IFMIS',
          description: 'Integrated Financial Management Information System',
          defaultEndpoint: 'https://ifmis.treasury.go.ke/api',
          defaultAuthUrl: 'https://ifmis.treasury.go.ke/oauth/authorize',
          defaultTokenUrl: 'https://ifmis.treasury.go.ke/oauth/token',
          defaultScope: 'read write'
        },
        {
          id: 'budget',
          name: 'Budget Management System',
          description: 'County Budget Planning and Management',
          defaultEndpoint: 'https://budget.county.go.ke/api'
        },
        {
          id: 'procurement',
          name: 'Procurement System',
          description: 'County Procurement Management',
          defaultEndpoint: 'https://procurement.county.go.ke/api'
        },
        {
          id: 'asset',
          name: 'Asset Management',
          description: 'County Asset Tracking System',
          defaultEndpoint: 'https://assets.county.go.ke/api'
        }
      ],

      governmentSystems: [
        {
          id: 'nema',
          name: 'NEMA',
          description: 'National Environment Management Authority',
          defaultEndpoint: 'https://nema.go.ke/api'
        },
        {
          id: 'kebs',
          name: 'KEBS',
          description: 'Kenya Bureau of Standards',
          defaultEndpoint: 'https://kebs.go.ke/api'
        },
        {
          id: 'nhif',
          name: 'NHIF',
          description: 'National Hospital Insurance Fund',
          defaultEndpoint: 'https://nhif.or.ke/api'
        },
        {
          id: 'nssf',
          name: 'NSSF',
          description: 'National Social Security Fund',
          defaultEndpoint: 'https://nssf.or.ke/api'
        }
      ],

      paymentSystems: [
        {
          id: 'airtel',
          name: 'Airtel Money',
          description: 'Airtel Mobile Money Integration',
          defaultEndpoint: 'https://api.airtel.co.ke/merchant'
        },
        {
          id: 'equity',
          name: 'Equity Bank',
          description: 'Equity Bank Payment Gateway',
          defaultEndpoint: 'https://api.equitybankgroup.com'
        },
        {
          id: 'kcb',
          name: 'KCB Bank',
          description: 'KCB Bank Integration',
          defaultEndpoint: 'https://api.kcbgroup.com'
        },
        {
          id: 'paypal',
          name: 'PayPal',
          description: 'PayPal Payment Processing',
          defaultEndpoint: 'https://api.paypal.com'
        }
      ],

      thirdPartySystems: [
        {
          id: 'custom_api',
          name: 'Custom API',
          description: 'Custom REST API Integration',
          defaultEndpoint: 'https://your-api.com/api'
        },
        {
          id: 'webhook',
          name: 'Webhook Receiver',
          description: 'Receive data via webhooks',
          defaultEndpoint: 'https://your-webhook-endpoint.com'
        },
        {
          id: 'database',
          name: 'External Database',
          description: 'Direct database connection',
          defaultEndpoint: 'jdbc:postgresql://host:port/database'
        },
        {
          id: 'file_system',
          name: 'File System',
          description: 'File-based data exchange',
          defaultEndpoint: '/path/to/shared/folder'
        }
      ]
    }
  },
  computed: {
    canProceed() {
      switch (this.currentStep) {
        case 'type':
          return this.selectedSystem !== null;
        case 'config':
          return this.integrationForm.systemName && this.integrationForm.apiEndpoint;
        case 'auth':
          return this.authForm.authType && this.validateAuthForm();
        case 'review':
          return true;
        default:
          return false;
      }
    },

    canTestConnection() {
      return this.integrationForm.apiEndpoint && this.authForm.authType && this.validateAuthForm();
    }
  },
  methods: {
    selectCategory(category) {
      this.selectedCategory = category;
      // Auto-select first system if only one in category
      const systems = this.getSystemsByCategory(category);
      if (systems.length === 1) {
        this.selectSystem(systems[0]);
      }
    },

    selectSystem(system) {
      this.selectedSystem = system;
      this.selectedCategory = this.getCategoryBySystem(system);

      // Pre-fill form with system defaults
      this.integrationForm.systemName = system.name;
      this.integrationForm.description = system.description;
      this.integrationForm.apiEndpoint = system.defaultEndpoint || '';

      // Pre-fill auth form if defaults exist
      if (system.defaultAuthUrl) this.authForm.authUrl = system.defaultAuthUrl;
      if (system.defaultTokenUrl) this.authForm.tokenUrl = system.defaultTokenUrl;
      if (system.defaultScope) this.authForm.scope = system.defaultScope;
    },

    getSystemsByCategory(category) {
      switch (category) {
        case 'COUNTY_FINANCIAL':
          return this.countyFinancialSystems;
        case 'GOVERNMENT':
          return this.governmentSystems;
        case 'PAYMENT':
          return this.paymentSystems;
        case 'THIRD_PARTY':
          return this.thirdPartySystems;
        default:
          return [];
      }
    },

    getCategoryBySystem(system) {
      if (this.countyFinancialSystems.includes(system)) return 'COUNTY_FINANCIAL';
      if (this.governmentSystems.includes(system)) return 'GOVERNMENT';
      if (this.paymentSystems.includes(system)) return 'PAYMENT';
      if (this.thirdPartySystems.includes(system)) return 'THIRD_PARTY';
      return '';
    },

    nextStep() {
      switch (this.currentStep) {
        case 'type':
          if (this.selectedSystem) {
            this.stepCompleted.type = true;
            this.currentStep = 'config';
          }
          break;
        case 'config':
          if (this.validateConfiguration()) {
            this.stepCompleted.config = true;
            this.currentStep = 'auth';
          }
          break;
        case 'auth':
          if (this.validateAuthentication()) {
            this.stepCompleted.auth = true;
            this.currentStep = 'review';
          }
          break;
      }
    },

    previousStep() {
      switch (this.currentStep) {
        case 'config':
          this.currentStep = 'type';
          break;
        case 'auth':
          this.currentStep = 'config';
          break;
        case 'review':
          this.currentStep = 'auth';
          break;
      }
    },

    validateConfiguration() {
      return this.integrationForm.systemName && this.integrationForm.apiEndpoint;
    },

    validateAuthentication() {
      return this.authForm.authType && this.validateAuthForm();
    },

    validateAuthForm() {
      switch (this.authForm.authType) {
        case 'API_KEY':
          return this.authForm.apiKey && this.authForm.apiKeyHeader;
        case 'OAUTH2':
          return this.authForm.clientId && this.authForm.clientSecret && this.authForm.tokenUrl;
        case 'BASIC_AUTH':
          return this.authForm.username && this.authForm.password;
        case 'BEARER_TOKEN':
          return this.authForm.bearerToken;
        case 'CUSTOM':
          return this.authForm.customHeaders.length > 0 &&
              this.authForm.customHeaders.every(h => h.name && h.value);
        case 'NONE':
          return true;
        default:
          return false;
      }
    },

    handleAuthTypeChange() {
      // Reset form when auth type changes
      Object.keys(this.authForm).forEach(key => {
        if (key !== 'authType') {
          if (key === 'customHeaders') {
            this.authForm[key] = [];
          } else {
            this.authForm[key] = '';
          }
        }
      });
      this.connectionTestResult = null;
    },

    addCustomHeader() {
      this.authForm.customHeaders.push({ name: '', value: '' });
    },

    removeCustomHeader(index) {
      this.authForm.customHeaders.splice(index, 1);
    },

    async testConnection() {
      try {
        this.testing = true;
        this.connectionTestResult = null;

        // Simulate connection test
        await new Promise(resolve => setTimeout(resolve, 3000));

        // Random success/failure for demo
        const success = Math.random() > 0.3;

        this.connectionTestResult = {
          success: success,
          message: success
              ? 'Connection established successfully! Authentication verified.'
              : 'Connection failed. Please check your endpoint and credentials.',
          details: success
              ? {
                responseTime: Math.floor(Math.random() * 500) + 100,
                statusCode: 200,
                server: this.selectedSystem?.name || 'Unknown'
              }
              : {
                responseTime: 0,
                statusCode: Math.random() > 0.5 ? 401 : 404,
                server: null
              }
        };

        this.testing = false;
      } catch (error) {
        this.testing = false;
        this.connectionTestResult = {
          success: false,
          message: 'Connection error: ' + error.message,
          details: null
        };
      }
    },

    async createIntegration() {
      try {
        this.creating = true;

        // Simulate integration creation
        await new Promise(resolve => setTimeout(resolve, 3000));

        // Prepare integration data
        const integrationData = {
          systemName: this.integrationForm.systemName,
          description: this.integrationForm.description,
          category: this.selectedCategory,
          apiEndpoint: this.integrationForm.apiEndpoint,
          apiVersion: this.integrationForm.apiVersion,
          syncFrequency: this.integrationForm.syncFrequency,
          dataFlow: this.integrationForm.dataFlow,
          timeout: this.integrationForm.timeout,
          retryAttempts: this.integrationForm.retryAttempts,
          enableLogging: this.integrationForm.enableLogging,
          authentication: {
            type: this.authForm.authType,
            ...this.authForm
          },
          status: 'PENDING',
          health: 0
        };

        // Emit integration creation event
        this.$emit('integration-added', integrationData);

        this.creating = false;

        // Close modal
        this.closeModal();

        // Reset form
        this.resetForm();

      } catch (error) {
        this.creating = false;
        alert('Error creating integration: ' + error.message);
      }
    },

    closeModal() {
      const modalElement = document.getElementById('addIntegrationModal');
      if (modalElement) {
        const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
        if (bootstrapModal) {
          bootstrapModal.hide();
        }
      }
    },

    resetForm() {
      this.currentStep = 'type';
      this.selectedCategory = '';
      this.selectedSystem = null;
      this.connectionTestResult = null;

      this.stepCompleted = {
        type: false,
        config: false,
        auth: false,
        review: false
      };

      this.integrationForm = {
        systemName: '',
        description: '',
        apiEndpoint: '',
        apiVersion: 'v1',
        syncFrequency: 'DAILY',
        dataFlow: [],
        timeout: 30,
        retryAttempts: 3,
        enableLogging: true
      };

      this.authForm = {
        authType: '',
        apiKey: '',
        apiKeyHeader: 'X-API-Key',
        clientId: '',
        clientSecret: '',
        authUrl: '',
        tokenUrl: '',
        scope: '',
        username: '',
        password: '',
        bearerToken: '',
        customHeaders: []
      };
    },

    formatCategory(category) {
      const categories = {
        'COUNTY_FINANCIAL': 'County Financial',
        'GOVERNMENT': 'Government',
        'PAYMENT': 'Payment',
        'THIRD_PARTY': 'Third Party'
      };
      return categories[category] || category;
    },

    formatAuthType(authType) {
      const types = {
        'API_KEY': 'API Key',
        'OAUTH2': 'OAuth 2.0',
        'BASIC_AUTH': 'Basic Authentication',
        'BEARER_TOKEN': 'Bearer Token',
        'CUSTOM': 'Custom Headers',
        'NONE': 'No Authentication'
      };
      return types[authType] || authType;
    },

    formatDataFlow(dataFlow) {
      if (!dataFlow || dataFlow.length === 0) return 'None';
      if (dataFlow.includes('IN') && dataFlow.includes('OUT')) return 'Bidirectional';
      if (dataFlow.includes('IN')) return 'Inbound';
      if (dataFlow.includes('OUT')) return 'Outbound';
      return 'None';
    }
  }
}
</script>

<style scoped>
.integration-type-card {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.integration-type-card:hover {
  transform: translateY(-2px);
}

.integration-type-card .card {
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.integration-type-card .card:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.system-option {
  padding: 0.75rem;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.system-option:hover {
  background-color: #f8f9fa;
}

.form-check-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.step-indicators {
  display: flex;
  align-items: center;
  gap: 0;
}

.step-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0.5;
  transition: opacity 0.3s ease;
}

.step-indicator.active,
.step-indicator.completed {
  opacity: 1;
}

.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #6c757d;
  transition: all 0.3s ease;
}

.step-indicator.active .step-circle {
  background-color: #0d6efd;
  color: white;
}

.step-indicator.completed .step-circle {
  background-color: #198754;
  color: white;
}

.step-connector {
  width: 80px;
  height: 2px;
  background-color: #dee2e6;
  margin: 0 10px;
}

.step-label {
  font-size: 0.875rem;
  margin-top: 0.5rem;
  text-align: center;
  white-space: nowrap;
}

.step-number {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.875rem;
}

.tips-content .alert-sm {
  padding: 0.5rem;
  margin-bottom: 0.5rem;
}

.spinner-border-sm {
  width: 0.875rem;
  height: 0.875rem;
}

.table-borderless td,
.table-borderless th {
  border: none;
}

.text-truncate {
  max-width: 200px;
}

.modal-xl {
  max-width: 1200px;
}

.fs-1 {
  font-size: 2rem !important;
}

.border-2 {
  border-width: 2px !important;
}

.alert-sm {
  padding: 0.5rem;
  font-size: 0.875rem;
}
</style>